import moment from 'moment';
import { Platform } from 'react-native';
import { importAfterMetroReady } from '@utils/metroDynamicImport';

function makeMomentLocaleLoader(
	requireLocale: () => void,
	importLocale: () => Promise<unknown>
): () => Promise<unknown> {
	if (Platform.OS !== 'web') {
		return async () => {
			requireLocale();
		};
	}
	return () => importAfterMetroReady(importLocale);
}

const MOMENT_LOCALE_LOADERS: Record<string, () => Promise<unknown>> = {
  'en-GB': makeMomentLocaleLoader(() => require('moment/locale/en-gb'), () => import('moment/locale/en-gb')),
  en: makeMomentLocaleLoader(() => require('moment/locale/en-gb'), () => import('moment/locale/en-gb')),
  fr: makeMomentLocaleLoader(() => require('moment/locale/fr'), () => import('moment/locale/fr')),
  es: makeMomentLocaleLoader(() => require('moment/locale/es'), () => import('moment/locale/es')),
  de: makeMomentLocaleLoader(() => require('moment/locale/de'), () => import('moment/locale/de')),
  zh: makeMomentLocaleLoader(() => require('moment/locale/zh-cn'), () => import('moment/locale/zh-cn')),
  'zh-CN': makeMomentLocaleLoader(() => require('moment/locale/zh-cn'), () => import('moment/locale/zh-cn')),
  'zh-TW': makeMomentLocaleLoader(() => require('moment/locale/zh-tw'), () => import('moment/locale/zh-tw')),
  ja: makeMomentLocaleLoader(() => require('moment/locale/ja'), () => import('moment/locale/ja')),
  ko: makeMomentLocaleLoader(() => require('moment/locale/ko'), () => import('moment/locale/ko')),
  ru: makeMomentLocaleLoader(() => require('moment/locale/ru'), () => import('moment/locale/ru')),
  ar: makeMomentLocaleLoader(() => require('moment/locale/ar'), () => import('moment/locale/ar')),
  hi: makeMomentLocaleLoader(() => require('moment/locale/hi'), () => import('moment/locale/hi')),
  bn: makeMomentLocaleLoader(() => require('moment/locale/bn'), () => import('moment/locale/bn')),
  'pa-in': makeMomentLocaleLoader(() => require('moment/locale/pa-in'), () => import('moment/locale/pa-in')),
  ta: makeMomentLocaleLoader(() => require('moment/locale/ta'), () => import('moment/locale/ta')),
  te: makeMomentLocaleLoader(() => require('moment/locale/te'), () => import('moment/locale/te')),
  ur: makeMomentLocaleLoader(() => require('moment/locale/ur'), () => import('moment/locale/ur')),
  vi: makeMomentLocaleLoader(() => require('moment/locale/vi'), () => import('moment/locale/vi')),
  tr: makeMomentLocaleLoader(() => require('moment/locale/tr'), () => import('moment/locale/tr')),
  nl: makeMomentLocaleLoader(() => require('moment/locale/nl'), () => import('moment/locale/nl')),
  pl: makeMomentLocaleLoader(() => require('moment/locale/pl'), () => import('moment/locale/pl')),
  pt: makeMomentLocaleLoader(() => require('moment/locale/pt'), () => import('moment/locale/pt')),
  ro: makeMomentLocaleLoader(() => require('moment/locale/ro'), () => import('moment/locale/ro')),
  sk: makeMomentLocaleLoader(() => require('moment/locale/sk'), () => import('moment/locale/sk')),
  sl: makeMomentLocaleLoader(() => require('moment/locale/sl'), () => import('moment/locale/sl')),
  sv: makeMomentLocaleLoader(() => require('moment/locale/sv'), () => import('moment/locale/sv')),
  th: makeMomentLocaleLoader(() => require('moment/locale/th'), () => import('moment/locale/th')),
  tk: makeMomentLocaleLoader(() => require('moment/locale/tr'), () => import('moment/locale/tr')),
  el: makeMomentLocaleLoader(() => require('moment/locale/el'), () => import('moment/locale/el')),
  it: makeMomentLocaleLoader(() => require('moment/locale/it'), () => import('moment/locale/it')),
  ml: makeMomentLocaleLoader(() => require('moment/locale/ml'), () => import('moment/locale/ml')),
  si: makeMomentLocaleLoader(() => require('moment/locale/si'), () => import('moment/locale/si')),
  fil: makeMomentLocaleLoader(() => require('moment/locale/fil'), () => import('moment/locale/fil')),
  ms: makeMomentLocaleLoader(() => require('moment/locale/ms'), () => import('moment/locale/ms')),
};

function momentLocaleKey(appLocale: string): string {
  const normalized = String(appLocale || 'en-GB').trim();
  if (normalized === 'en-GB' || normalized === 'English') return 'en-gb';
  // Moment has no Xhosa/Zulu packs — keep English date formatting for those app locales.
  if (normalized === 'xh' || normalized === 'zu') return 'en-gb';
  if (normalized.startsWith('zh')) return normalized.includes('TW') ? 'zh-tw' : 'zh-cn';
  if (normalized === 'tk') return 'tr';
  return normalized.split('-')[0].toLowerCase();
}

export async function applyMomentLocale(appLocale?: string | null): Promise<void> {
  const key = momentLocaleKey(appLocale || 'en-GB');

  if (!moment.locales().includes(key)) {
    const loaderKey = Object.keys(MOMENT_LOCALE_LOADERS).find(
      (k) => k.toLowerCase() === key || k.toLowerCase() === String(appLocale || '').toLowerCase()
    ) ?? 'en-GB';

    try {
      await (MOMENT_LOCALE_LOADERS[loaderKey] ?? MOMENT_LOCALE_LOADERS['en-GB'])();
    } catch {
      moment.locale('en');
      return;
    }
  }

  moment.locale(key);
}
